import { moneyLib, validationLib } from "@agentcall/core/lib"
import { tenantType } from "@agentcall/core/type"
import { zodResolver } from "@hookform/resolvers/zod"
import { Search } from "lucide-react"
import { useState } from "react"
import { Plus, Trash } from "react-feather"
import { useFieldArray, useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"
import { TenantUsageSelector } from "@/components/room"
import {
  RoomSearchModal,
  type SelectedRoom,
} from "@/components/room/room-search-modal/RoomSearchModal"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox/Checkbox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input/Input"

// フォームの入居者入力を API のリクエスト形式へ変換する。
export const toOccupantsPayload = (
  occupants: Array<{
    name: string
    nameKana: string
    phone: string
    birthday: string
    note: string
  }>
) =>
  occupants.map((occupant) => ({
    name: occupant.name,
    nameKana: occupant.nameKana,
    phone: occupant.phone === "" ? null : occupant.phone,
    birthday: occupant.birthday === "" ? null : occupant.birthday,
    note: occupant.note === "" ? null : occupant.note,
    gender: null,
  }))

// API のレスポンスをフォームの入居者入力へ変換する。
export const toOccupantsFormValues = (
  occupants:
    | Array<{
        name: string
        nameKana: string
        phone?: string | null
        birthday: string | null
        note?: string | null
      }>
    | undefined
) =>
  (occupants ?? []).map((occupant) => ({
    name: occupant.name,
    nameKana: occupant.nameKana,
    phone: occupant.phone ?? "",
    birthday: occupant.birthday ?? "",
    note: occupant.note ?? "",
  }))

const getDigits = (value: string, maxLength: number) =>
  value.replace(/[^0-9]/g, "").slice(0, maxLength)

// フォームスキーマ
export const tenantFormSchema = z
  .object({
    roomId: z.string().optional(),
    roomName: z.string().optional(),
    propertyName: z.string().optional(),
    purpose: z.string().min(1, "用途を選択してください"),
    name: z.string().min(1, "氏名を入力してください"),
    nameKana: z
      .string()
      .min(1, "氏名（カナ）を入力してください")
      .regex(validationLib.KATAKANA_REGEX, validationLib.KATAKANA_ERROR_MESSAGE),
    phone1: tenantType.phone1Z,
    phone2: tenantType.phone2Z,
    phone3: tenantType.phone3Z,
    phone4: tenantType.phone4Z,
    phone5: tenantType.phone5Z,
    phone6: tenantType.phone6Z,
    email: z
      .string()
      .refine(
        (val) => val === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
        "有効なメールアドレスを入力してください"
      ),
    birthday: z.string().optional(),
    startDate: z.string().min(1, "契約開始日を入力してください"),
    rent: z.string().optional(),
    commonAreaFee: z.string().optional(),
    isParking: z.boolean(),
    parkingNumber: z.string().optional(),
    parkingFee: z.string().optional(),
    otherFees: z.array(
      z.object({
        name: z.string().min(1, "費用名を入力してください"),
        baseAmount: z.string().min(1, "基本料金を入力してください"),
        tax: z.string().min(1, "税額を入力してください"),
      })
    ),
    occupants: z.array(
      z.object({
        name: z.string().min(1, "入居者の氏名を入力してください"),
        nameKana: z.string(),
        phone: z.string(),
        birthday: z.string(),
        note: z.string(),
      })
    ),
  })
  .refine(
    (data) => {
      if (data.isParking) {
        return data.parkingNumber && data.parkingNumber.length > 0
      }
      return true
    },
    {
      message: "区画番号を入力してください",
      path: ["parkingNumber"],
    }
  )
  .refine(
    (data) => {
      if (data.isParking) {
        return data.parkingFee && data.parkingFee.length > 0
      }
      return true
    },
    {
      message: "駐車場代を入力してください",
      path: ["parkingFee"],
    }
  )

export type TenantFormValues = z.infer<typeof tenantFormSchema>

// 税込金額を計算（null/undefined/空文字対応）
const calcTaxIncludedAmount = (price: string | number | undefined | null): number => {
  const numericPrice = typeof price === "string" ? Number.parseFloat(price) : price
  if (!numericPrice || Number.isNaN(numericPrice)) return 0
  return moneyLib.calculateTaxIncluded(numericPrice)
}

// 税額を計算（null/undefined/空文字対応）
const calcTax = (price: string | number | undefined | null): number => {
  const numericPrice = typeof price === "string" ? Number.parseFloat(price) : price
  if (!numericPrice || Number.isNaN(numericPrice)) return 0
  return moneyLib.calculateTax(numericPrice)
}

// 金額フォーマット
const formatAmount = (amount: number): string => {
  return `¥${amount.toLocaleString("ja-JP")}`
}

// 税抜き金額をフォーマット
const formatPrice = (price: string | number | undefined | null): string => {
  const numericPrice = typeof price === "string" ? Number.parseFloat(price) : price
  if (!numericPrice || Number.isNaN(numericPrice)) return "¥0"
  return `¥${numericPrice.toLocaleString("ja-JP")}`
}

interface TenantFormProps {
  roomId?: number
  defaultValues?: Partial<TenantFormValues>
  onSubmit: (values: TenantFormValues) => Promise<void>
  onCancel: () => void
  isSubmitting: boolean
  submitLabel?: string
}

export function TenantForm({
  roomId,
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel = "登録する",
}: TenantFormProps) {
  const [isRoomSearchModalOpen, setIsRoomSearchModalOpen] = useState(false)

  const form = useForm<TenantFormValues>({
    resolver: zodResolver(tenantFormSchema),
    defaultValues: {
      roomId: defaultValues?.roomId ?? "",
      roomName: defaultValues?.roomName ?? "",
      propertyName: defaultValues?.propertyName ?? "",
      purpose: defaultValues?.purpose ?? "",
      name: defaultValues?.name ?? "",
      nameKana: defaultValues?.nameKana ?? "",
      phone1: defaultValues?.phone1 ?? "",
      phone2: defaultValues?.phone2 ?? "",
      phone3: defaultValues?.phone3 ?? "",
      phone4: defaultValues?.phone4 ?? "",
      phone5: defaultValues?.phone5 ?? "",
      phone6: defaultValues?.phone6 ?? "",
      email: defaultValues?.email ?? "",
      birthday: defaultValues?.birthday ?? "",
      startDate: defaultValues?.startDate ?? "",
      rent: defaultValues?.rent ?? "",
      commonAreaFee: defaultValues?.commonAreaFee ?? "",
      isParking: defaultValues?.isParking ?? false,
      parkingNumber: defaultValues?.parkingNumber ?? "",
      parkingFee: defaultValues?.parkingFee ?? "",
      otherFees: defaultValues?.otherFees ?? [],
      occupants: defaultValues?.occupants ?? [],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "otherFees",
  })

  const {
    fields: occupantFields,
    append: appendOccupant,
    remove: removeOccupant,
  } = useFieldArray({
    control: form.control,
    name: "occupants",
  })

  const watchPurpose = form.watch("purpose")
  const watchIsParking = form.watch("isParking")
  const watchParkingFee = form.watch("parkingFee")
  const watchRent = form.watch("rent")
  const watchCommonAreaFee = form.watch("commonAreaFee")

  const handleAddOtherFee = () => {
    if (fields.length >= 3) {
      toast.error("その他費用は最大3件までしか追加できません")
      return
    }
    append({
      name: "",
      baseAmount: "",
      tax: "",
    })
  }

  const handleFormSubmit = (values: TenantFormValues) => {
    // 空文字列をnullに変換（birthdayフィールド）
    const sanitizedValues = {
      ...values,
      birthday: values.birthday || undefined,
    }
    return onSubmit(sanitizedValues)
  }

  const handleSelectRoom = (room: SelectedRoom) => {
    form.setValue("roomId", room.id)
    form.setValue("roomName", room.name)
    form.setValue("propertyName", room.propertyName)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
        {/* 部屋選択カード（roomIdが渡されていない場合のみ表示） */}
        {!roomId && (
          <Card className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
            <CardHeader className="border-b border-slate-200 py-2.5 px-5">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-[#3f4652]">
                  部屋情報
                  <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                    必須
                  </span>
                </h2>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsRoomSearchModalOpen(true)}
                  className="h-8"
                >
                  <Search size={14} />
                  部屋を検索
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-5">
              <FormField
                control={form.control}
                name="roomId"
                render={() => (
                  <FormItem className="hidden">
                    <FormControl>
                      <Input type="hidden" />
                    </FormControl>
                  </FormItem>
                )}
              />
              {form.formState.errors.roomId && (
                <p className="text-sm font-medium text-destructive mb-4">
                  {form.formState.errors.roomId.message}
                </p>
              )}
              {form.watch("roomId") ? (
                <div className="grid grid-cols-3 gap-x-8 gap-y-4">
                  <div>
                    <p className="text-xs font-medium text-[#9eafc7] mb-2">部屋ID</p>
                    <p className="text-sm text-[#3f4652]">{form.watch("roomId")}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-[#9eafc7] mb-2">物件名</p>
                    <p className="text-sm text-[#3f4652]">{form.watch("propertyName")}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-[#9eafc7] mb-2">部屋番号</p>
                    <p className="text-sm text-[#3f4652]">{form.watch("roomName")}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-8">部屋を検索してください</p>
              )}
            </CardContent>
          </Card>
        )}
        {/* 賃借人情報 Card */}
        <div className="bg-white rounded-[10px] shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
          <div className="px-5 py-2.5 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-[#3f4652]">賃借人情報</h3>
          </div>
          <div className="p-5 space-y-4">
            {/* 用途 */}
            <div className="grid grid-cols-3 gap-x-4">
              <FormField
                control={form.control}
                name="purpose"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-[#3f4652]">
                      用途
                      <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                        必須
                      </span>
                    </FormLabel>
                    <FormControl>
                      <TenantUsageSelector value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* 氏名・氏名(カナ) */}
            <div className="grid grid-cols-3 gap-x-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-[#3f4652]">
                      氏名
                      <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                        必須
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="nameKana"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-[#3f4652]">
                      氏名(カナ)
                      <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                        必須
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="ヤマダ タロウ" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* 電話番号 */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-[#3f4652]">
                電話番号1
                <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                  必須
                </span>
              </p>
              <div className="grid grid-cols-3 gap-x-4">
                <FormField
                  control={form.control}
                  name="phone1"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          type="tel"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="03"
                          onChange={(e) => field.onChange(getDigits(e.target.value, 4))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone2"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          type="tel"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="1234"
                          onChange={(e) => field.onChange(getDigits(e.target.value, 4))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone3"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          type="tel"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="5678"
                          onChange={(e) => field.onChange(getDigits(e.target.value, 4))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-semibold text-[#3f4652]">電話番号2</p>
              <div className="grid grid-cols-3 gap-x-4">
                <FormField
                  control={form.control}
                  name="phone4"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          type="tel"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="03"
                          onChange={(e) => field.onChange(getDigits(e.target.value, 4))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone5"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          type="tel"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="1234"
                          onChange={(e) => field.onChange(getDigits(e.target.value, 4))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone6"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          type="tel"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="5678"
                          onChange={(e) => field.onChange(getDigits(e.target.value, 4))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* メールアドレス・生年月日 */}
            <div className="grid grid-cols-3 gap-x-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-[#3f4652]">
                      メールアドレス
                    </FormLabel>
                    <FormControl>
                      <Input type="email" {...field} placeholder="yamada@example.com" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="birthday"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-[#3f4652]">生年月日</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* 初回賃貸借契約開始日 */}
            <div className="grid grid-cols-3 gap-x-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-[#3f4652]">
                      初回賃貸借契約開始日
                      <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                        必須
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        {/* 料金情報 Card */}
        <div className="bg-white rounded-[10px] shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
          <div className="px-5 py-2.5 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-[#3f4652]">料金情報</h3>
          </div>
          <div className="p-5 space-y-4">
            {/* 家賃 */}
            <div className="flex gap-2.5 items-end">
              <div className="flex-1">
                <FormField
                  control={form.control}
                  name="rent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-semibold text-[#3f4652]">家賃</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} unit="円" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {watchPurpose === tenantType.TENANT_PURPOSE_BUSINESS && (
                <div className="flex-1 pb-2.5">
                  <div className="text-base font-semibold text-right text-slate-600">
                    {formatAmount(calcTaxIncludedAmount(watchRent))}
                    <span className="text-sm text-slate-400">
                      （{formatPrice(watchRent)} + 税{formatAmount(calcTax(watchRent))}）
                    </span>
                  </div>
                </div>
              )}
              {watchPurpose === tenantType.TENANT_PURPOSE_LIVING && (
                <div className="flex-1 pb-2.5">
                  <div className="text-base font-semibold text-right text-slate-600">
                    {formatPrice(watchRent)}
                  </div>
                </div>
              )}
            </div>

            {/* 共益費・管理費 */}
            <div className="flex gap-2.5 items-end">
              <div className="flex-1">
                <FormField
                  control={form.control}
                  name="commonAreaFee"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-semibold text-[#3f4652]">
                        共益費・管理費
                      </FormLabel>
                      <FormControl>
                        <Input type="number" {...field} unit="円" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {watchPurpose === tenantType.TENANT_PURPOSE_BUSINESS && (
                <div className="flex-1 pb-2.5">
                  <div className="text-base font-semibold text-right text-slate-600">
                    {formatAmount(calcTaxIncludedAmount(watchCommonAreaFee))}
                    <span className="text-sm text-slate-400">
                      （{formatPrice(watchCommonAreaFee)} + 税
                      {formatAmount(calcTax(watchCommonAreaFee))}）
                    </span>
                  </div>
                </div>
              )}
              {watchPurpose === tenantType.TENANT_PURPOSE_LIVING && (
                <div className="flex-1 pb-2.5">
                  <div className="text-base font-semibold text-right text-slate-600">
                    {formatPrice(watchCommonAreaFee)}
                  </div>
                </div>
              )}
            </div>

            {/* 駐車場あり */}
            <FormField
              control={form.control}
              name="isParking"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Checkbox
                      label="駐車場あり"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* 駐車場情報 */}
            {watchIsParking && (
              <div className="flex gap-2.5 items-end">
                <div className="flex gap-[30px] flex-1">
                  <div className="flex-1">
                    <FormField
                      control={form.control}
                      name="parkingNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            区画番号
                          </FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex-1">
                    <FormField
                      control={form.control}
                      name="parkingFee"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            駐車場代
                          </FormLabel>
                          <FormControl>
                            <Input type="number" {...field} unit="円" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <div className="flex-1 pb-2.5">
                  <div className="text-base font-semibold text-right text-slate-600">
                    {formatAmount(calcTaxIncludedAmount(watchParkingFee))}
                    <span className="text-sm text-slate-400">
                      （{formatPrice(watchParkingFee)} + 税{formatAmount(calcTax(watchParkingFee))}
                      ）
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* その他費用 */}
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="border border-slate-200 rounded-[10px] p-3.5 space-y-3.5"
              >
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-slate-600">その他費用</h4>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="w-4 h-4"
                    onClick={() => remove(index)}
                  >
                    <Trash size={16} className="text-[#FF3232]" />
                  </Button>
                </div>
                <div className="flex gap-3.5">
                  <div className="flex-1">
                    <FormField
                      control={form.control}
                      name={`otherFees.${index}.name`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            費用名
                          </FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex-1">
                    <FormField
                      control={form.control}
                      name={`otherFees.${index}.baseAmount`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            基本料金
                          </FormLabel>
                          <FormControl>
                            <Input type="number" {...field} unit="円" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex-1">
                    <FormField
                      control={form.control}
                      name={`otherFees.${index}.tax`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            税額
                          </FormLabel>
                          <FormControl>
                            <Input type="number" {...field} unit="円" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <div className="pt-1.5">
                  <div className="text-base font-semibold text-right text-slate-600">
                    {formatAmount(
                      (Number.parseFloat(form.watch(`otherFees.${index}.baseAmount`) || "0") || 0) +
                        (Number.parseFloat(form.watch(`otherFees.${index}.tax`) || "0") || 0)
                    )}
                    <span className="text-sm text-slate-400">
                      （{formatPrice(form.watch(`otherFees.${index}.baseAmount`))} + 税
                      {formatAmount(
                        Number.parseFloat(form.watch(`otherFees.${index}.tax`) || "0") || 0
                      )}
                      ）
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {/* その他費用を追加ボタン */}
            <Button
              type="button"
              variant="outline"
              className="w-full border-slate-400 font-bold"
              onClick={handleAddOtherFee}
            >
              <Plus size={14} className="mr-2 font-bold" />
              その他費用を追加
            </Button>
          </div>
        </div>

        {/* 入居者 */}
        <div className="space-y-4">
          <h3 className="text-base font-semibold text-slate-600">入居者</h3>
          <div className="space-y-4">
            {occupantFields.length === 0 && (
              <p className="text-sm text-slate-400">入居者の登録はありません</p>
            )}

            {occupantFields.map((occupantField, index) => (
              <div
                key={occupantField.id}
                className="space-y-3 rounded-md border border-slate-200 p-4"
              >
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-slate-600">入居者{index + 1}</h4>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="w-4 h-4"
                    onClick={() => removeOccupant(index)}
                  >
                    <Trash size={16} className="text-[#FF3232]" />
                  </Button>
                </div>
                <div className="flex gap-3.5">
                  <div className="flex-1">
                    <FormField
                      control={form.control}
                      name={`occupants.${index}.name`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            氏名
                          </FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="鈴木一郎" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex-1">
                    <FormField
                      control={form.control}
                      name={`occupants.${index}.nameKana`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            氏名（カナ）
                          </FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="スズキイチロウ" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <div className="flex gap-3.5">
                  <div className="flex-1">
                    <FormField
                      control={form.control}
                      name={`occupants.${index}.phone`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            電話番号
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="tel"
                              inputMode="numeric"
                              maxLength={20}
                              placeholder="09012345678"
                              onChange={(e) => field.onChange(getDigits(e.target.value, 20))}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex-1">
                    <FormField
                      control={form.control}
                      name={`occupants.${index}.birthday`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            生年月日
                          </FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <FormField
                  control={form.control}
                  name={`occupants.${index}.note`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-semibold text-[#3f4652]">備考</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="備考" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              className="w-full border-slate-400 font-bold"
              onClick={() =>
                appendOccupant({
                  name: "",
                  nameKana: "",
                  phone: "",
                  birthday: "",
                  note: "",
                })
              }
            >
              <Plus size={14} className="mr-2 font-bold" />
              入居者を追加
            </Button>
          </div>
        </div>

        {/* アクションボタン */}
        <div className="flex justify-end gap-4">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "登録中..." : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            キャンセル
          </Button>
        </div>
      </form>

      {/* 部屋検索モーダル */}
      <RoomSearchModal
        open={isRoomSearchModalOpen}
        onOpenChange={setIsRoomSearchModalOpen}
        onSelect={handleSelectRoom}
      />
    </Form>
  )
}
